/**
 * Deterministic drill-canvas synthesizer.
 *
 * Brain-planned session drills (draft_session_plan) and any other metadata-only
 * drill have no `drill_spec`, so they render empty. Given a drill's type / phase
 * / player count this builds a sensible, realistic-looking layout — players in a
 * formation appropriate to the drill type, cones/goals, a ball, and a movement
 * arrow — so the drill renders in the preview AND the canvas. Pure + stable
 * (ids derived from index, no randomness), so the same metadata always yields
 * the same canvas.
 *
 * Uses `pitchViewExtended:'custom'` + `customPitch` so the preview and the
 * editor render it identically (custom view ignores the team pitch backdrop).
 */
import { DRILL_SPEC_SCHEMA_VERSION, type DrillArrow, type DrillCone, type NormPoint } from '@4cm/core/drills/types';
import type { DrillRolePlayer } from './playerRoles';
import type { FullDrillSpec } from './fullDrillSpec';

export interface SynthMeta {
  drillType?: string | null;
  primaryPhase?: string | null;
  minPlayers?: number | null;
  maxPlayers?: number | null;
  name?: string | null;
}

type DrillType = 'WARM_UP' | 'TECHNICAL' | 'TACTICAL' | 'SMALL_SIDED_GAME' | 'COOL_DOWN';

const DEFAULT_COUNT: Record<DrillType, number> = {
  WARM_UP: 8,
  TECHNICAL: 4,
  TACTICAL: 8,
  SMALL_SIDED_GAME: 8,
  COOL_DOWN: 6,
};

const PITCH: Record<DrillType, { width: number; length: number }> = {
  WARM_UP: { width: 25, length: 22 },
  TECHNICAL: { width: 22, length: 30 },
  TACTICAL: { width: 44, length: 55 },
  SMALL_SIDED_GAME: { width: 32, length: 42 },
  COOL_DOWN: { width: 20, length: 20 },
};

function normType(raw: string | null | undefined): DrillType {
  const up = String(raw ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  return (['WARM_UP', 'TECHNICAL', 'TACTICAL', 'SMALL_SIDED_GAME', 'COOL_DOWN'] as DrillType[]).includes(up as DrillType)
    ? (up as DrillType)
    : 'SMALL_SIDED_GAME';
}

function resolveCount(m: SynthMeta, type: DrillType): number {
  const max = typeof m.maxPlayers === 'number' && m.maxPlayers > 0 ? m.maxPlayers : null;
  const min = typeof m.minPlayers === 'number' && m.minPlayers > 0 ? m.minPlayers : null;
  const c = max ?? min ?? DEFAULT_COUNT[type];
  return Math.max(2, Math.min(12, Math.round(c)));
}

function player(id: string, roleId: string, pos: NormPoint, label?: string): DrillRolePlayer {
  return label ? { id, roleId, position: pos, label } : { id, roleId, position: pos };
}
function cone(id: string, pos: NormPoint, variant: DrillCone['variant'] = 'cone'): DrillCone {
  return { id, position: pos, variant };
}
/** Evenly spread n x-positions across the width, inset from the touchlines. */
function spread(n: number): number[] {
  if (n <= 1) return [0.5];
  return Array.from({ length: n }, (_, i) => 0.18 + (0.64 * i) / (n - 1));
}

export function synthesizeDrillSpec(meta: SynthMeta): FullDrillSpec {
  const type = normType(meta.drillType);
  const count = resolveCount(meta, type);
  const base: Pick<FullDrillSpec, 'schemaVersion' | 'customPitch' | 'pitchViewExtended' | 'entities' | 'zones' | 'texts' | 'keyframes'> = {
    schemaVersion: DRILL_SPEC_SCHEMA_VERSION,
    customPitch: PITCH[type],
    pitchViewExtended: 'custom',
    entities: [],
    zones: [],
    texts: [],
    keyframes: [],
  };

  if (type === 'COOL_DOWN') {
    const players = Array.from({ length: count }, (_, i) => {
      const a = (2 * Math.PI * i) / count;
      return player(`syn-p${i}`, 'attacker', [0.5 + 0.32 * Math.cos(a), 0.5 + 0.32 * Math.sin(a)], String(i + 1));
    });
    return { ...base, cones: [cone('syn-c0', [0.5, 0.5], 'flat')], arrows: [], rolePlayers: players };
  }

  if (type === 'WARM_UP') {
    const perRow = Math.ceil(count / 2);
    const xsTop = spread(perRow);
    const xsBot = spread(count - perRow);
    const players: DrillRolePlayer[] = [
      ...xsTop.map((x, i) => player(`syn-t${i}`, 'attacker', [x, 0.35], String(i + 1))),
      ...xsBot.map((x, i) => player(`syn-b${i}`, 'attacker', [x, 0.7], String(perRow + i + 1))),
    ];
    const cones: DrillCone[] = spread(4).map((x, i) => cone(`syn-c${i}`, [x, 0.52], i % 2 === 0 ? 'cone' : 'cone_disc'));
    return {
      ...base,
      cones,
      arrows: [{ id: 'syn-a0', from: { kind: 'point', position: [xsTop[0], 0.35] }, to: { kind: 'point', position: [xsTop[0], 0.7] }, kind: 'run', style: 'dashed', label: 'move' }],
      rolePlayers: [...players, player('syn-ball', 'ball', [xsTop[0] + 0.03, 0.35])],
    };
  }

  if (type === 'TECHNICAL') {
    const workers = Math.max(2, count - 1);
    const ys = workers <= 1 ? [0.5] : Array.from({ length: workers }, (_, i) => 0.25 + (0.5 * i) / (workers - 1));
    const players: DrillRolePlayer[] = [
      player('syn-srv', 'server', [0.5, 0.08], 'S'),
      ...ys.map((y, i) => player(`syn-w${i}`, 'attacker', [0.5, y], String(i + 1))),
      player('syn-ball', 'ball', [0.52, 0.1]),
    ];
    const cones: DrillCone[] = ys.flatMap((y, i) => [
      cone(`syn-cl${i}`, [0.32, y]),
      cone(`syn-cr${i}`, [0.68, y]),
    ]);
    return {
      ...base,
      cones,
      arrows: [
        { id: 'syn-a0', from: { kind: 'entity', entityId: 'syn-srv' }, to: { kind: 'entity', entityId: 'syn-w0' }, kind: 'pass', style: 'solid', label: '1' },
        { id: 'syn-a1', from: { kind: 'entity', entityId: 'syn-w0' }, to: { kind: 'point', position: [0.5, ys[ys.length - 1]] }, kind: 'dribble', style: 'solid', curvature: 0.25, label: '2' },
      ],
      rolePlayers: players,
    };
  }

  // SMALL_SIDED_GAME + TACTICAL — two opposing units + goal(s) + ball.
  const phase = String(meta.primaryPhase ?? '').toLowerCase();
  const attackers = phase === 'defending' ? Math.floor(count / 2) : Math.ceil(count / 2);
  const defenders = count - attackers;
  const xsA = spread(attackers);
  const xsD = spread(defenders);
  const players: DrillRolePlayer[] = [
    ...xsA.map((x, i) => player(`syn-a${i}`, 'attacker', [x, 0.4], String(i + 1))),
    ...xsD.map((x, i) => player(`syn-d${i}`, 'defender', [x, 0.66], String(i + 1))),
    player('syn-ball', 'ball', [xsA[0] + 0.03, 0.42]),
  ];
  const cones: DrillCone[] = [
    cone('syn-g0', [0.5, 0.97], 'goal_mini'),
    ...(type === 'TACTICAL' ? [cone('syn-g1', [0.5, 0.03], 'goal_mini')] : []),
  ];
  const arrows: DrillArrow[] = [
    { id: 'syn-pass', from: { kind: 'entity', entityId: 'syn-a0' }, to: { kind: 'entity', entityId: xsA.length > 1 ? 'syn-a1' : 'syn-a0' }, kind: 'pass', style: 'solid', label: '1' },
  ];
  if (phase === 'defending' || type === 'TACTICAL') {
    arrows.push({ id: 'syn-press', from: { kind: 'entity', entityId: 'syn-d0' }, to: { kind: 'entity', entityId: 'syn-a0' }, kind: 'press', style: 'dashed', color: '#C0392B', label: 'press' });
  }
  if (phase === 'transitions') {
    arrows.push({ id: 'syn-break', from: { kind: 'entity', entityId: 'syn-a0' }, to: { kind: 'point', position: [0.5, 0.92] }, kind: 'run', style: 'dashed', color: '#F39C12', label: 'break' });
  }
  // Mark the playing area as a zone so the grid reads clearly.
  const zones = [{ id: 'syn-zone', shape: { kind: 'rect' as const, topLeft: [0.08, 0.12] as NormPoint, bottomRight: [0.92, 0.9] as NormPoint }, stroke: '#2ECC71', strokeStyle: 'dashed' as const }];
  return { ...base, zones, cones, arrows, rolePlayers: players };
}
